import { v4 as uuid } from 'uuid'
import { AuthResponse } from '@/hexapla-core/domains/accounts'
import { DEFAULT_NEW_LEMMA, FetchQueue, FlashMessage, HexaplaState } from '@/store/state'
import { Lemma } from '@/hexapla-core/domains/lemmas'
import { Contributor } from '@/hexapla-core/domains/accounts'
import { isFellow } from '@/hexapla-core/domains/accounts/roles'
import { Book, Meta, Translator, TranslatorRelationship, Witness, WitnessType } from '@/hexapla-core/domains/resources'
import { Reader } from '../state'
import { MutationTree } from 'vuex'

export enum MutationTypes {
  UNLOCK = 'UNLOCK',
  LOCK = 'LOCK',
  SESSION_ENDED = 'SESSION_ENDED',
  REGISTER_VIEW_MESSAGE = 'REGISTER_VIEW_MESSAGE',
  CLOSE_VIEW_MESSAGE = 'CLOSE_VIEW_MESSAGE',
  PUSH_FLASH = 'PUSH_FLASH',
  REMOVE_FLASH = 'REMOVE_FLASH',
  SET_LEMMAS = 'SET_LEMMAS',
  TOGGLE_APP_IS_LOADING = 'TOGGLE_APP_IS_LOADING',
  SET_CONTRIBUTOR = 'SET_CONTRIBUTOR',
  SET_BOOKS = 'SET_BOOKS',
  SET_TRANSLATORS = 'SET_TRANSLATORS',
  SET_TRANSLATOR_RELATIONSHIPS = 'SET_TRANSLATOR_RELATIONSHIPS',
  SET_WITNESSES = 'SET_WITNESSES',
  SET_WITNESS_TYPES = 'SET_WITNESS_TYPES',
  SET_READER_META = 'SET_READER_META',
  UPDATE_READER = 'UPDATE_READER',
  UPDATE_NEW_LEMMA = 'UPDATE_NEW_LEMMA',
  RESET_NEW_LEMMA = 'RESET_NEW_LEMMA',
  SET_EDITING_LEMMA = 'SET_EDITING_LEMMA',
  SETTINGS__TOGGLE = 'settings/toggle',
  PRE_FETCH_QUEUE__SET = 'preFetchQueue/set',
  PRE_FETCH_QUEUE__ADD = 'preFetchQueue/add',
  PRE_FETCH_QUEUE__NAMED = 'preFetchQueue/named',
  PRE_FETCH_QUEUE__POP = 'preFetchQueue/pop',
  PRE_FETCH_QUEUE__CLEAR = 'preFetchQueue/clear',
}

// `user` signed out. Shared by LOCK (the user signed out) and SESSION_ENDED (the
// server rejected the session), which differ only in what happens to the lemma
// being built.
const signedOut = (user: HexaplaState['user']): HexaplaState['user'] => ({
  ...user,
  isLoggedIn: false,
  token: '',
  contributor: { assignments: [] },
  roles: [],
  lemmas: [],
  isFellow: false,
  supervisorId: null,
  isSupervisor: false,
  scopedBookIds: [],
})

export default {
  [MutationTypes.UNLOCK]: (state: HexaplaState, { contributor, token, scoped_book_ids }: AuthResponse) => {
    const { user } = state
    const { roles, lemmas, settings, ...rest } = contributor

    // A lemma kept through an ended session belongs to whoever was signed in.
    // Someone else signing in on this browser starts from a clean wizard.
    if (state.draftOwnerId !== null && state.draftOwnerId !== Number(contributor.id)) {
      state.newLemma = { ...DEFAULT_NEW_LEMMA }
      state.editingLemmaId = null
    }
    state.draftOwnerId = null

    user.token = token
    user.isLoggedIn = true
    user.contributor = rest
    user.roles = roles || []
    user.lemmas = lemmas || []

    // Supervision context. `scopedBookIds` mirrors the server's answer for
    // this user — for a fellow that is their granted subset, never their
    // supervisor's full set. Used to hide affordances, never to protect data:
    // HexaplaApi.Policy re-decides every one of these server-side.
    user.isFellow = isFellow(roles)
    user.supervisorId = rest.supervisor_id ?? null
    user.isSupervisor = Boolean(rest.fellows && rest.fellows.length)
    // Prefer the server's computed scope: for a fellow it is the grant
    // intersected with the supervisor's books, which raw assignment rows are
    // not (an orphaned fellow still has rows but no scope). Fall back to the
    // rows only for an API that predates the field.
    user.scopedBookIds =
      scoped_book_ids !== undefined
        ? scoped_book_ids
        : (rest.assignments || []).map((b) => b.id as number)

    if (!settings || !Object.keys(settings).length) {
      return (user.settings = {
        'menus.side': true,
        'menus.side.translators': true,
        'menus.side.witnesses': true,
        'menus.side.texts': true,
        'menus.top': false,
      })
    }

    user.settings = settings
  },
  // Deliberate sign-out: nothing of this user's is left behind.
  [MutationTypes.LOCK]: (state: HexaplaState) => {
    state.user = signedOut(state.user)
    state.newLemma = { ...DEFAULT_NEW_LEMMA }
    state.draftOwnerId = null
    state.editingLemmaId = null
  },
  // The server rejected the session — expired, or replaced by a sign-in
  // elsewhere — possibly in the middle of building a lemma. Clearing the lemma
  // here (as LOCK does) threw that work away. It is kept, marked with its
  // owner, for UNLOCK to restore to the same contributor only.
  [MutationTypes.SESSION_ENDED]: (state: HexaplaState) => {
    const ownerId = state.user.contributor?.id
    if (ownerId !== null && ownerId !== undefined) state.draftOwnerId = Number(ownerId)
    state.user = signedOut(state.user)
  },
  [MutationTypes.REGISTER_VIEW_MESSAGE]: ({ viewMessages }: HexaplaState, view: string) => (viewMessages[view] = true),
  [MutationTypes.CLOSE_VIEW_MESSAGE]: ({ viewMessages }: HexaplaState, view: string) => (viewMessages[view] = false),
  [MutationTypes.PUSH_FLASH]: (state: HexaplaState, flash: FlashMessage) => {
    state.flashQueue = [...state.flashQueue, flash]
  },
  [MutationTypes.REMOVE_FLASH]: (state: HexaplaState, index: number) => {
    state.flashQueue = state.flashQueue.filter((_message, i) => {
      return index !== i
    })
  },
  [MutationTypes.SET_LEMMAS]: ({ user }: HexaplaState, lemmas: Lemma[]) => (user.lemmas = lemmas),
  [MutationTypes.TOGGLE_APP_IS_LOADING]: (state: HexaplaState) => (state.appIsLoading = !state.appIsLoading),
  [MutationTypes.SET_CONTRIBUTOR]: ({ user }: HexaplaState, contributor: Contributor) => {
    user.contributor = contributor
  },
  [MutationTypes.SET_BOOKS]: (state: HexaplaState, books: Book[]) => {
    state.books = books
    // Note: Contributors (including admins) use their actual assignments from the API
    // Do NOT override assignments here - only assigned books should appear in lemma builder
  },
  [MutationTypes.SET_TRANSLATORS]: (state: HexaplaState, translators: Translator[]) =>
    (state.translators = translators),
  [MutationTypes.SET_TRANSLATOR_RELATIONSHIPS]: (state: HexaplaState, rels: TranslatorRelationship[]) =>
    (state.translatorRelationships = rels),
  [MutationTypes.SET_WITNESSES]: (state: HexaplaState, witnesses: Witness[]) => (state.witnesses = witnesses),
  [MutationTypes.SET_WITNESS_TYPES]: (state: HexaplaState, types: WitnessType[]) => (state.witnessTypes = types),
  [MutationTypes.SET_READER_META]: (state: HexaplaState, meta: Meta) => (state.reader.meta = meta),
  [MutationTypes.UPDATE_READER]: (state: HexaplaState, reader: Partial<Reader>) =>
    (state.reader = { ...state.reader, ...reader }),
  [MutationTypes.UPDATE_NEW_LEMMA]: (state: HexaplaState, newLemma: Partial<Lemma>) =>
    (state.newLemma = { ...state.newLemma, ...newLemma }),
  // The wizard's lemma is finished with (saved and handed off, or deleted). Its
  // id must not survive: the wizard resumes whatever `newLemma` holds, and a
  // stale id turns the next "new" lemma into an update of this one.
  [MutationTypes.RESET_NEW_LEMMA]: (state: HexaplaState) => {
    state.newLemma = { ...DEFAULT_NEW_LEMMA }
    state.draftOwnerId = null
    state.editingLemmaId = null
  },
  // The contributor opened an existing lemma with Edit: holding it is intended.
  [MutationTypes.SET_EDITING_LEMMA]: (state: HexaplaState, id: Lemma['id'] | null) => {
    state.editingLemmaId = id
  },
  [MutationTypes.SETTINGS__TOGGLE]: ({ user }: HexaplaState, setting: string) =>
    (user.settings[setting] = !user.settings[setting]),
  [MutationTypes.PRE_FETCH_QUEUE__SET]: (store: HexaplaState, queues: FetchQueue[]) => (store.preFetchQueue = queues),
  [MutationTypes.PRE_FETCH_QUEUE__ADD]: (store: HexaplaState, action: <T>() => Promise<T>) =>
    (store.preFetchQueue = [...store.preFetchQueue, { id: uuid(), action }]),
  [MutationTypes.PRE_FETCH_QUEUE__NAMED]: (
    store: HexaplaState,
    { id, action }: { id: string; action: <T>() => Promise<T> },
  ) => (store.preFetchQueue = [...store.preFetchQueue, { id, action }]),
  [MutationTypes.PRE_FETCH_QUEUE__POP]: (store: HexaplaState, id: string) =>
    (store.preFetchQueue = store.preFetchQueue.filter((pfq) => pfq.id !== id)),
  [MutationTypes.PRE_FETCH_QUEUE__CLEAR]: (store: HexaplaState) => (store.preFetchQueue = []),
} as MutationTree<HexaplaState>
